/**
 * Typed API client for the Lokma harness server (Fastify).
 * Single HTTP entry point for the web app — all panes go through here (DRY).
 * Real endpoints only: every function calls a live `/api/*` URL, never mock data.
 * Auth: httpOnly cookie (sent via `credentials: include`) + optional Bearer token
 * from `localStorage["lokma-token"]` (`lokma auth <token>` stores it there).
 */

export type ApiErrorShape = { code: string; message: string };

/** REQ-193 slice 9 — server route the pane iframe loads (server `PROXY_PATH`). */
const BROWSER_PROXY_PATH = '/api/browser/proxy';
/**
 * REQ-193 slice 9 — response header carrying the login-wall verdict. Mirrors the
 * server's `LOGIN_WALL_HEADER`; declared here rather than imported because this
 * module is the self-contained client layer and must not depend on components.
 * The name is asserted equal to the server constant in `browser.test.ts`, so the
 * two copies cannot drift silently.
 */
const LOGIN_WALL_HEADER = 'x-lokma-login-wall';

/** Typed fetch error — carries the server `{ code, message }` shape. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  /**
   * Raw parsed error body when the server sent one (REQ-062: 409
   * `todo_claimed` carries `holder`, so the board can name the session
   * that holds the lease). Undefined for non-JSON failures.
   */
  readonly details?: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const TOKEN_KEY = 'lokma-token';
const LOGIN_PATH = '/login';

/** Read the stored Bearer token without crashing outside the browser. */
function readToken(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Bounce to login on 401 (browser only, never loops on the login page itself). */
function redirectToLogin(): void {
  try {
    if (typeof window === 'undefined' || !window.location) return;
    if (window.location.pathname === LOGIN_PATH) return;
    window.location.href = LOGIN_PATH;
  } catch {
    // Non-browser runtimes (tests, SSR probes) skip the redirect.
  }
}

/** Normalize every server failure shape into `{ code, message }`. */
async function toApiError(res: Response, redirect = true): Promise<ApiError> {
  const status = res.status;
  if (status === 401) {
    if (redirect) redirectToLogin();
    return new ApiError('unauthorized', 'Not signed in — redirected to login', 401);
  }
  let code = 'http_error';
  let message = `HTTP ${status}`;
  let details: unknown;
  try {
    const text = await res.text();
    if (text) {
      try {
        const body = JSON.parse(text) as Record<string, unknown>;
        details = body;
        if (typeof body.code === 'string' && typeof body.message === 'string') {
          code = body.code;
          message = body.message;
        } else if (typeof body.error === 'string') {
          // Legacy server shape `{ ok: false, error: "..." }` (Phase 0 routes).
          message = body.error;
          code = typeof body.code === 'string' ? body.code : 'request_failed';
        } else if (typeof body.message === 'string') {
          message = body.message;
        } else {
          message = text.slice(0, 300);
        }
      } catch {
        message = text.slice(0, 300);
      }
    }
  } catch {
    // Keep the default HTTP message when the body is unreadable.
  }
  return new ApiError(code, message, status, details);
}

/**
 * Core request helper — GET/POST/PATCH/DELETE with auth + 401 handling.
 * Throws ApiError on any non-2xx response. Pass `{ redirect401: false }`
 * for expected-401 probes (e.g. the AccountPane logged-out check) so the
 * global login bounce does not fire.
 */
export async function request<T>(
  path: string,
  init: RequestInit = {},
  opts: { redirect401?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const token = readToken();
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(path, { ...init, headers, credentials: 'include' });
  if (!res.ok) throw await toApiError(res, opts.redirect401 !== false);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function get<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

async function put<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

async function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

/** Back-compat thin wrapper — kept for existing callers, now with auth + 401 handling. */
export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, init);
}

/**
 * Authenticated fetch for non-JSON payloads (file downloads like usage
 * export). Applies the same cookie + Bearer auth as `request`, throws
 * ApiError on non-2xx. Keeps auth logic DRY — panes never hand-roll it.
 */
export async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = readToken();
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(path, { ...init, headers, credentials: 'include' });
  if (!res.ok) throw await toApiError(res);
  return res;
}

// ─── Response types (mirror live server route shapes) ───────────────────────

export type HealthRes = { ok: boolean; service: string; version: string };
/** Live host numbers from `GET /api/metrics` (REQ-018 status bar). */
export type MetricsRes = {
  version: string;
  uptimeSec: number;
  /** System CPU % since the previous poll — null on the first sample. */
  cpuPercent: number | null;
  memory: { totalBytes: number; usedBytes: number };
  process: { rssBytes: number; heapUsedBytes: number };
};
export type ConfigRes = {
  config: unknown;
  credentials: Record<string, { keySet: boolean; last4: string | null }>;
};
export type ProviderInfo = {
  id: string;
  name: string;
  baseUrl: string;
  enabled: boolean;
  keySet: boolean;
  last4: string | null;
  priority: number;
  custom: boolean;
};
export type ProvidersRes = { providers: ProviderInfo[] };
export type ProviderTestRes = {
  ok: boolean;
  provider: string;
  modelCount?: number;
  models?: string[];
  latencyMs?: number;
  error?: string;
  note?: string;
};
export type ProviderMutationRes = { ok: boolean; provider: ProviderInfo };
export type CreateProviderBody = { id: string; name: string; baseUrl: string; apiKey?: string; enabled?: boolean };
export type PatchProviderBody = { name?: string; baseUrl?: string; enabled?: boolean; priority?: number; apiKey?: string };
export type ModelInfo = { id: string; label: string; provider: string; enabled: boolean; unsupported?: boolean };
export type ModelsRes = { models: ModelInfo[]; count: number; enabledCount: number; cached: boolean };
export type ModelsMutationRes = {
  ok: boolean;
  updated: number;
  /** Ids the server skipped because they are not in the live catalog (partial-success bulk). */
  skipped?: number;
  skippedIds?: string[];
  models: ModelInfo[];
  count: number;
  enabledCount: number;
};
/** One provider row in the `POST /api/models/refresh` response (REQ-032). */
export type ModelsRefreshProvider = {
  id: string;
  ok: boolean;
  modelCount: number;
  latencyMs: number;
  error?: string;
  /** True when the provider was never probed (no stored key). */
  skipped?: boolean;
};
/** Live fan-out refresh — merged catalog plus per-provider probe outcomes. */
export type ModelsRefreshRes = {
  ok: boolean;
  models: ModelInfo[];
  count: number;
  enabledCount: number;
  providers: ModelsRefreshProvider[];
  refreshedAt: string;
};
export type SessionSummary = {
  id: string;
  cwd?: string;
  /** Display title (server derives it from the first user line when never renamed). */
  title?: string;
  /** True when the title was set via rename. */
  renamed?: boolean;
  /** Per-session model from the meta sidecar (null when never set). */
  model?: string | null;
  /** Bot binding id (REQ-027) — null when the session is plain chat. */
  botId?: string | null;
  /** Owner user id (REQ-062 Parça B) — null for legacy/anonymous sessions. */
  ownerId?: string | null;
  /** Live run flag (REQ-121) — true while the agent works this session. */
  running?: boolean;
  /** Queued prompts behind the live run (REQ-121). */
  queued?: number;
  messageCount?: number;
  /** ISO timestamps for Today/Yesterday/Earlier grouping. */
  createdAt?: string;
  updatedAt?: string;
};
export type SessionsRes = { sessions: SessionSummary[]; count: number };
export type SessionDetail = { id: string; cwd: string; model: string | null; botId: string | null; messages: unknown[]; count: number };
/** REQ-070: backend run status — a run in flight or prompts waiting behind it. */
export type SessionRunStatus = { sessionId: string; running: boolean; queued: number };
export type CreateSessionRes = { ok: boolean; id: string; cwd: string };
export type ForkSessionRes = { ok: boolean; id: string; from: string; copied?: number };
export type PatchSessionRes = { ok: boolean; id: string; model: string; title?: string | null; botId?: string | null };
export type RenameSessionRes = { ok: boolean; id: string; model: string; title: string | null };
export type DeleteSessionRes = { ok: boolean; id: string };
export type MergeSessionRes = { ok: boolean; id: string; from: string; appended: number };
export type RewindSessionRes = { ok: boolean; id: string; kept: number };
export type CompactionAnchor = { role: string; toolName?: string; excerpt: string; timestamp: string };
export type CompactionStatusRes = {
  id: string;
  messages: number;
  chars: number;
  hygieneNeeded: boolean;
  summaryNeeded: boolean;
  last: {
    compactedAt: string;
    mode: string;
    compacted: boolean;
    beforeMessages: number;
    afterMessages: number;
    beforeChars: number;
    afterChars: number;
    archived: number;
    archiveMessages: number;
  } | null;
};
export type CompactionRunRes = {
  ok: boolean;
  id: string;
  compactedAt: string;
  mode: string;
  compacted: boolean;
  beforeMessages: number;
  afterMessages: number;
  beforeChars: number;
  afterChars: number;
  archived: number;
  archiveMessages: number;
  anchors: CompactionAnchor[];
  summary: string | null;
};
export type SessionSearchHit = {
  sessionId: string;
  title: string;
  role: string;
  toolName?: string;
  index: number;
  excerpt: string;
  timestamp: string;
  score: number;
};
export type SessionSearchRes = { hits: SessionSearchHit[]; count: number; engine: 'fts5' | 'substring' };
export type SlashCommandInfo = { id: string; name: string; hint: string; usage: string };
export type CommandsRes = { commands: SlashCommandInfo[]; count: number };
export type AgentInfo = { id: string; [k: string]: unknown };
export type AgentCaps = { maxAgents: number; maxConcurrent: number; maxQueue: number };
export type AgentsRes = { agents: AgentInfo[]; count: number; caps: AgentCaps };
export type AgentMutationRes = { ok: boolean; agent: AgentInfo; action?: string; from?: string };
export type DeleteAgentRes = { ok: boolean; id: string };
export type CreateAgentBody = {
  id?: string;
  name: string;
  persona?: string;
  model?: string;
  cwd?: string;
  budgets?: { tokens?: number; usd?: number };
  soul?: string;
  memory?: string;
  /** Lineage tag (`fork:<id>` / `fanout:<stem>` / `ai:<id>`) — server records it. */
  createdBy?: string;
};
export type PatchAgentBody = { name?: string; model?: string; budgets?: { tokens?: number; usd?: number } };
export type AgentDocRes = { ok: boolean; id: string; doc: string; content: string };
export type AgentDocWriteRes = { ok: boolean; id: string; doc: string; bytes: number };
export type SkillInfo = {
  id: string;
  name: string;
  description: string;
  category: string;
  path: string;
  linked_files: string[];
  [k: string]: unknown;
};
/** Per-skill curator counters (`~/.lokma/skills/.usage.json`, Hermes shape). */
export type SkillUsage = {
  use_count: number;
  view_count: number;
  patch_count: number;
  last_used?: string;
};
export type SkillsRes = { skills: SkillInfo[]; count?: number; usage?: Record<string, SkillUsage> };
export type SkillDetailRes = { ok: boolean; skill: SkillInfo; content: string };
export type SkillFileRes = { ok: boolean; path: string; content: string };
export type SkillPatchRes = { ok: boolean; skill: SkillInfo; bytes: number };
export type SkillUseRes = { ok: boolean; id: string };
export type SkillPatchBody = { old_string: string; new_string: string };
/** Installed skill row + its `~/.lokma/skills/<slug>/` dir name. */
export type SkillInstallRes = { ok: boolean; skill: SkillInfo; slug: string };
/** One named theme from `GET /api/themes` (canonical core registry + card preview). */
export type ThemeView = {
  id: string;
  name: string;
  label: string;
  description: string;
  mode: 'light' | 'dark';
  cssVars: Record<string, string>;
  chalk: Record<string, string>;
  preview: { bg: string; accent: string };
};
export type ThemesRes = { ok: boolean; themes: ThemeView[]; count: number; default: string | null };
export type ThemeDetailRes = { ok: boolean; theme: ThemeView };
export type VaultGraphRes = { nodes: unknown[]; links: unknown[]; count?: number; note?: string };
export type VaultTreeEntry = { name: string; path: string; type: 'dir' | 'note'; children?: VaultTreeEntry[] };
export type VaultTreeRes = { ok: boolean; tree: VaultTreeEntry[] };
export type VaultNoteRes = {
  ok: boolean;
  path: string;
  title: string;
  tags: string[];
  links: string[];
  provenance: string | null;
  size: number;
  mtimeMs: number;
  content: string;
  truncated: boolean;
};
export type VaultSearchHit = {
  path: string;
  title: string;
  tags: string[];
  links: string[];
  provenance: string | null;
  size: number;
  mtimeMs: number;
  score: number;
  snippet: string;
};
export type VaultSearchRes = {
  ok: boolean;
  q: string;
  folder: string;
  hits: VaultSearchHit[];
  count: number;
  engine: 'fts5' | 'substring';
};
export type VaultIngestBody = { path: string; content: string; provenance?: string };
export type VaultIngestRes = { ok: boolean; path: string; bytes: number; created: boolean };
export type MemoryTarget = 'memory' | 'user';
export type MemoryUsageRes = {
  ok: boolean;
  target: MemoryTarget;
  entries: string[];
  count: number;
  chars: number;
  limit: number;
  usage: string;
};
export type MemoryAddBody = { target?: MemoryTarget; content: string };
export type MemoryReplaceBody = { target?: MemoryTarget; old_text: string; content: string };
export type MemoryRemoveBody = { target?: MemoryTarget; old_text: string };
export type UsageModelRow = {
  model: string;
  family: string;
  runs: number;
  tokens: number;
  costUsd: number;
  share: number;
};
export type UsageDayPoint = { day: string; total: number; byModel: Record<string, number> };
export type UsageSummary = {
  rangeDays: number;
  runs: number;
  sessions: number;
  inputTokens: number;
  outputTokens: number;
  tokens: number;
  costUsd: number;
  avgPerSession: number;
  topModel: string | null;
  byModel: UsageModelRow[];
  series: UsageDayPoint[];
  unpricedTokens: number;
};
export type UsageSummaryRes = { ok: boolean; range: string; summary: UsageSummary };
export type UsageSessionRow = {
  sessionId: string;
  title: string;
  model: string;
  runs: number;
  tokens: number;
  costUsd: number;
  lastActive: string;
};
export type UsageSessionsRes = { ok: boolean; range: string; sessions: UsageSessionRow[]; count: number };
export type UsageRange = '7d' | '30d' | '90d';
export type UsageExportFormat = 'csv' | 'jsonl';

// ─── Workspace files (real FS behind the FileBrowser pane, W3-9) ────────────

export type GitState = 'M' | 'A' | 'D' | 'R' | '?';
export type FileEntry = {
  name: string;
  /** Workspace-relative `/`-separated path (matches `@mention` syntax). */
  path: string;
  type: 'file' | 'dir';
  size: number;
  mtimeMs: number;
  /** Null = clean (or not a repo) — never faked, straight from git status. */
  git: GitState | null;
};
export type FilesRes = { ok: boolean; path: string; entries: FileEntry[] };
/** REQ-084: server directory browser — dirs only, dot-entries hidden. */
export type FsDirEntry = { name: string; path: string };
export type FsListRes = {
  ok: boolean;
  path: string;
  parent: string | null;
  home: string;
  entries: FsDirEntry[];
  truncated: boolean;
};
export type FileContentRes = {
  ok: boolean;
  path: string;
  content: string;
  /** sha256 of the FULL file — pass back as `expectedSha` on save. */
  sha: string;
  size: number;
  truncated: boolean;
};
export type FileWriteRes = { ok: boolean; path: string; sha: string; size: number; created: boolean };
export type FileSearchHit = { path: string; type: 'file' | 'dir'; score: number };
export type FileSearchRes = { ok: boolean; q: string; hits: FileSearchHit[] };

// ─── Terminals (live shell processes behind the TerminalPane, W3-10) ──────

export type TerminalStatus = 'running' | 'exited' | 'error';
export type TerminalInfo = {
  id: string;
  shell: string;
  cwd: string;
  pid: number | null;
  /** True when the shell runs inside a real PTY (REQ-059), false on plain pipes. */
  pty: boolean;
  agentId: string | null;
  sessionId: string;
  status: TerminalStatus;
  startedAt: string;
  exitCode: number | null;
  signal: string | null;
  cols: number;
  rows: number;
};
export type TerminalsRes = { ok: boolean; terminals: TerminalInfo[]; count: number };
export type TerminalDetailRes = { ok: boolean; terminal: TerminalInfo; tail: string };
export type CreateTerminalBody = { cwd?: string; agentId?: string; sessionId?: string; cols?: number; rows?: number };
export type CreateTerminalRes = { ok: boolean; terminal: TerminalInfo };
export type TerminalInputRes = { ok: boolean; id: string; bytes: number };
export type DeleteTerminalRes = { ok: boolean; id: string; killed: boolean; exitCode: number | null; signal: string | null };

// ─── Browser tabs (per-agent live tabs behind the BrowserPane, W3-12) ───

export type BrowserTab = {
  id: string;
  url: string;
  /** Full navigation history, oldest first (server-owned, real pointer). */
  history: string[];
  /** Index into `history` pointing at `url`. */
  index: number;
  agentId: string | null;
  sessionId: string;
  /** Workspace scope label shown under each tab. */
  cwd: string | null;
  /**
   * REQ-154: last time the server browser engine acted on this tab — the
   * pane shows an "engine" badge from it (engine view ≠ this iframe view).
   */
  lastAgentUseAt?: string | null;
  /**
   * REQ-193 slice 10 (Kapsam 5): when the AGENT opened/navigated this tab.
   * Drives the pane's "agent" badge, so a page the agent chose is visibly
   * distinguishable from one the user typed. Optional on the wire for older
   * records — absent means "not opened by an agent", never "unknown".
   */
  openedByAgentAt?: string | null;
  createdAt: string;
  updatedAt: string;
};
export type BrowserTabsRes = { ok: boolean; tabs: BrowserTab[]; count: number };
export type BrowserTabRes = { ok: boolean; tab: BrowserTab };
export type OpenBrowserTabBody = { url?: string; agentId?: string; sessionId?: string; cwd?: string };
export type OpenBrowserTabRes = { ok: boolean; tabId: string; tab: BrowserTab };
export type CloseBrowserTabRes = { ok: boolean; id: string; closed: boolean };
/**
 * REQ-193 slice 9 — the server's verdict on a page, asked without its body.
 * `loginWall` comes from a response HEADER, so it is data rather than something
 * the pane parses out of a sentence or infers from the rendered form.
 */
export type BrowserPageProbe = { status: number; finalUrl: string; loginWall: boolean };

// ─── Setup + doctor (optional stack + init + probes behind the SetupPane, W6-22) ───

export type SetupFeatureView = {
  id: string;
  label: string;
  desc: string;
  docs: string;
  defaultOn: boolean;
  enabled: boolean;
};
export type SetupRes = { features: SetupFeatureView[]; applied: Record<string, boolean>; count: number };
export type SetupSaveRes = { ok: boolean; applied: Record<string, boolean> };
export type SetupInitBody = { cwd?: string };
export type SetupInitRes = { ok: boolean; created: string[]; existed: string[] };
export type DoctorCheckView = { name: string; ok: boolean; latencyMs: number; detail: string };
export type DoctorRes = { checks: DoctorCheckView[]; passed: number; total: number };

// ─── Cloud transfer (portable ~/.lokma export/import behind the SetupPane, Phase 3 cloud prep) ───

export type CloudImportBody = { zipBase64: string; overwrite?: boolean };
export type CloudRejectedEntry = { path: string; reason: string };
export type CloudImportRes = {
  ok: boolean;
  created: string[];
  skipped: string[];
  overwritten: string[];
  rejected: CloudRejectedEntry[];
  count: number;
};

// ─── Public tunnel (portable remote access, REQ-193 Kapsam 4) ──────────────
// Mirrors `TunnelStatus` in `packages/lokma-core/src/cloud/tunnel.ts`. Kept as
// a structural copy (not a cross-package import) so the web bundle never pulls
// a server module in; the route probe is what proves the two agree.
export type TunnelState = 'stopped' | 'starting' | 'running' | 'error';
export type TunnelProvider = 'cloudflared' | 'ngrok' | 'relay';
export type TunnelStatusRes = {
  state: TunnelState;
  /** Only ever read from provider stdout server-side; null when stopped. */
  url: string | null;
  provider: TunnelProvider | null;
  startedAt: string | null;
  expiresAt: string | null;
  /** Human sentence for the panel — honest on every branch. */
  message: string;
  /** Install/configure command when nothing could be started, else null. */
  installHint: string | null;
};
export type TunnelStartBody = { port?: number };

// ─── Archify diagrams (typed IR → validated HTML/SVG behind the ArchifyPane, W5-17) ───

export type ArchifyNode = { id: string; label: string; kind?: string };
export type ArchifyEdge = { from: string; to: string; label?: string };
export type ArchifyIR = {
  type: string;
  preset: string;
  theme: string;
  title: string;
  nodes: ArchifyNode[];
  edges: ArchifyEdge[];
  trace?: string[];
};
export type ArchifyReceiptRow = { gate: string; status: 'pass' | 'fail'; msg: string };
export type ArchifyGateError = { gate: string; message: string };
export type DiagramSummary = {
  id: string;
  type: string;
  preset: string;
  theme: string;
  title: string;
  nodeCount: number;
  edgeCount: number;
  updatedAt: string;
};
export type DiagramsRes = { items: DiagramSummary[]; count: number };
export type DiagramDetailRes = { ok: boolean; id: string; ir: ArchifyIR; receipt: ArchifyReceiptRow[]; html: string };
export type GenerateDiagramBody = { type: string; prompt: string; preset?: string; theme?: string };
export type GenerateDiagramRes = { ok: boolean; id: string; ir: ArchifyIR };
export type ValidateDiagramRes = { ok: boolean; errors: ArchifyGateError[]; receipt: ArchifyReceiptRow[] };
export type SaveDiagramRes = { ok: boolean; id: string; receipt: ArchifyReceiptRow[] };
export type DiagramDiff = { added: string[]; removed: string[]; changed: string[]; rerouted: string[] };
export type DiagramDeltaRes = { ok: boolean; diff: DiagramDiff; deltaHtml: string };
export type ArchifyGuideRes = { ok: boolean; id: string; starter: string };
export type ArchifyExportFormat = 'svg' | 'html' | 'json' | 'card' | 'png' | 'webm';

// ─── Design Studio (6 artifact types over bundled systems + DESIGN.md guard, W5-18) ───

/**
 * REQ-192 — one design skill as the generation actually recorded it. `sentChars`
 * is the honesty signal: a body the prompt budget truncated still counts as
 * sent, and a skill the resolver refused never appears here at all.
 */
export type DesignSkillApplied = {
  id: string;
  name: string;
  /** Characters of SKILL.md handed to the model (the per-skill cap may cut it). */
  sentChars: number;
  /** SKILL.md bytes on disk, when known. */
  bytes?: number;
};
/**
 * REQ-192 slice 5 — one design template as the generation recorded it. Same
 * contract as `DesignSkillApplied`: `sentChars` proves what reached the model
 * (the prompt cap may truncate a fat skeleton), and a template the resolver
 * refused never appears at all.
 */
export type DesignTemplateApplied = {
  id: string;
  label: string;
  /** Characters of skeleton handed to the model. */
  sentChars: number;
};
export type DesignManifest = {
  id: string;
  type: string;
  brief: string;
  system: string;
  createdAt: string;
  updatedAt: string;
  /** REQ-178 — resolved project cwd the artifact lives under (absent = global root). */
  project?: string;
  /**
   * REQ-192 — the design skills this artifact was generated with, as the SERVER
   * recorded them (id + name + how many characters of SKILL.md reached the
   * model). This is the honesty channel: the UI reads the names from here, so
   * it never claims a skill was applied when the resolver refused it. Absent
   * on pre-REQ-192 artifacts.
   */
  skills?: DesignSkillApplied[];
  /**
   * REQ-192 slice 5 — the TEMPLATE this artifact was generated with, as the
   * SERVER recorded it (id + label + the characters of skeleton that reached
   * the model). The same honesty channel as `skills`: absent means nothing was
   * sent (a pre-slice-5 artifact, or a refused template).
   */
  template?: DesignTemplateApplied;
};
export type DesignSummary = DesignManifest & { bytes: number; overall: number | null };
export type DesignsRes = { items: DesignSummary[]; count: number; project?: string | null; root?: string };
export type CritiqueScore = { dim: string; score: number; fixes: string[] };
export type CritiqueResult = { overall: number; scores: CritiqueScore[] };
export type DesignDetailRes = {
  ok: boolean;
  id: string;
  manifest: DesignManifest;
  html: string;
  critique: CritiqueResult | null;
  /** REQ-190 — sha256 of the loaded body; the tweak's optimistic lock. */
  sha?: string;
  /** REQ-190 — highest version index in the ledger (0 = no history yet). */
  currentVersion?: number;
};
/**
 * REQ-192 — `skills` is the design-SKILL axis (ids only; the server resolves
 * each to its SKILL.md body before the prompt is built). Omitted when empty so
 * a no-skill generation keeps the exact wire shape it had before.
 */
export type GenerateDesignBody = {
  type: string;
  brief: string;
  system?: string;
  model?: string;
  cwd?: string;
  skills?: string[];
};
export type GenerateDesignRes = { ok: boolean; id: string; manifest: DesignManifest; critique: CritiqueResult };
export type SaveDesignRes = {
  ok: boolean;
  id: string;
  manifest: DesignManifest;
  critique: CritiqueResult;
  /** REQ-190 — sha256 of the body just written; the lock token for the next write. */
  sha?: string;
  currentVersion?: number;
};
export type CritiqueDesignRes = { ok: boolean; id: string; critique: CritiqueResult };
/**
 * REQ-190 — one ledger entry. `n` is monotonic (v1 = the first body) and
 * `origin` says what produced it, so the picker can label the history without
 * guessing. A pre-REQ-190 artifact reports `versions: []` (honest, never a
 * faked v1) and its first edit simply starts the ledger at v1.
 */
export type DesignVersion = {
  n: number;
  sha: string;
  bytes: number;
  createdAt: string;
  origin: 'generate' | 'tweak' | 'edit' | 'revert';
  note?: string;
  model?: string;
  overall: number | null;
};
export type DesignVersionsRes = {
  ok: boolean;
  id: string;
  versions: DesignVersion[];
  currentVersion: number;
  sha: string;
};
export type DesignTweakBody = { note: string; model?: string; cwd?: string; expectedSha?: string };
export type DesignTweakRes = {
  ok: boolean;
  id: string;
  sha: string;
  currentVersion: number;
  /** Section numbers the model rewrote (`[]` in whole-document mode). */
  replaced: number[];
  note: string;
  model: string;
};
export type DesignRevertRes = {
  ok: boolean;
  id: string;
  manifest: DesignManifest;
  critique: CritiqueResult;
  currentVersion: number;
  restoredFrom: number;
};
export type DesignSystemMeta = {
  id: string;
  name: string;
  preset: string;
  tokens: string;
  bg: string;
  surface: string;
  ink: string;
  muted: string;
  accent: string;
  accentSoft: string;
  line: string;
  font: string;
};
/** REQ-191 — a catalog row. `origin` is the honesty channel: `bundled` rows
 * are the built-in presets, never an installed package. */
export type DesignSystemCatalogRow = {
  id: string;
  label: string;
  category: string;
  description: string;
  origin: 'catalog' | 'bundled';
  files: string[];
  status: 'ok' | 'invalid';
  problem?: string;
};
export type DesignSystemsRes = {
  ok: boolean;
  systems: DesignSystemCatalogRow[];
  count: number;
  /** `catalog` = read from disk, `bundled` = the fallback preset table. */
  source: 'catalog' | 'bundled';
  root: string;
  categories: string[];
};
/**
 * REQ-192 — one design-SKILL catalog row (the SECOND axis beside systems: a
 * system is a palette, a skill is a `SKILL.md` instruction set). `hasBody` is
 * the honesty channel: a row can be selected but still carry a `problem`, and
 * the server will refuse it with 409 rather than pretend it applied.
 */
export type DesignSkillRow = {
  id: string;
  name: string;
  description: string;
  group: string;
  category: string;
  bytes: number;
  linkedFiles: string[];
  hasBody: boolean;
  problem?: string;
};
export type DesignSkillsRes = {
  ok: boolean;
  skills: DesignSkillRow[];
  count: number;
  /** Always `design` today — the scope a SKILL.md must declare to be listed. */
  scope: string;
  groups: string[];
  /** Scanned skills excluded for lacking `scope: design` (the honest "why"). */
  unscoped: number;
  /** Scoped rows whose SKILL.md is missing/unreadable/oversized. */
  invalid: number;
};
/**
 * REQ-192 slice 5 — one design-TEMPLATE catalog row (the THIRD axis: a
 * template is the OUTPUT SKELETON, not a palette and not a style). `hasBody`
 * is the honesty channel, same as skills: a row can be picked but the server
 * answers 409 rather than claim the template shaped anything.
 */
export type DesignTemplateRow = {
  id: string;
  label: string;
  description: string;
  mode: string;
  category: string;
  examplePrompt: string;
  bytes: number;
  hasBody: boolean;
  origin: 'bundled' | 'installed';
  problem?: string;
};
export type DesignTemplatesRes = {
  ok: boolean;
  templates: DesignTemplateRow[];
  count: number;
  root: string;
  origins: string[];
  /** Rows whose SKILL.md is missing/unreadable/oversized. */
  invalid: number;
};
export type DesignSystemInstallRes = {
  ok: boolean;
  id: string;
  files: string[];
  source: string;
};
export type DesignSystemUseRes = {
  ok: boolean;
  id: string;
  cwd: string;
  copied: string[];
  tokens: string | null;
};
export type DesignGuard = {
  cwd: string;
  present: boolean;
  h2Count: number;
  sections: string[];
  ok: boolean;
  message: string;
};
export type DesignGuardRes = { ok: boolean; guard: DesignGuard };
export type DesignExportFormat = 'html' | 'zip' | 'json' | 'png' | 'webm';

// ─── Testing Lab (Plan→Run→Classify→Report over live handlers, W5-19) ───

export type TestClassification = 'contract' | 'env' | 'fragility';
export type TestResult = {
  name: string;
  kind: 'http' | 'shannon';
  status: 'pass' | 'fail';
  ms: number;
  detail: string;
  classification?: TestClassification;
};
export type ShannonFinding = { pattern: string; location: string };
export type TestReport = {
  id: string;
  plan: string;
  createdAt: string;
  durationMs: number;
  tests: TestResult[];
  pass: number;
  fail: number;
  /** Rerun-history diffing is a follow-up — always 0, never invented. */
  flaky: number;
  shannon: string;
  shannonFindings: ShannonFinding[];
};
export type TestPlanDoc = {
  id: string;
  plan: string;
  targets: string[];
  includeShannon: boolean;
  createdAt: string;
};
export type TestSummary = {
  id: string;
  plan: string;
  tests: number;
  pass: number;
  fail: number;
  flaky: number;
  dur: string;
  shannon: string;
  createdAt: string;
};
export type TestsRes = { items: TestSummary[]; count: number };
export type RunTestBody = { plan: string; targets?: string[]; includeShannon?: boolean; timeoutMs?: number };
export type RunTestRes = { ok: boolean; id: string; report: TestReport };
export type TestDetailRes = { ok: boolean; plan: TestPlanDoc | null; report: TestReport };

// ─── Repo git (real status/log/commit/push behind the GitPane, W3-11) ─────

export type GitFileChange = { path: string; staged: string | null; worktree: string | null };
export type GitStatusRes =
  | {
      ok: boolean;
      repo: true;
      cwd: string;
      branch: string;
      upstream: string | null;
      ahead: number;
      behind: number;
      files: GitFileChange[];
      counts: { changed: number; staged: number; unstaged: number };
      worktrees: string[];
    }
  | { ok: boolean; repo: false; cwd: string };
export type GitLogEntry = { hash: string; short: string; message: string; author: string; date: string };
export type GitLogRes = { ok: boolean; branch: string; commits: GitLogEntry[] };
export type GitCommitRes = { ok: boolean; hash: string; short: string; message: string };
export type GitPushRes = { ok: boolean; pushed: boolean; output: string };
export type GitGcRes = { ok: boolean; pruned: boolean };
export type GitLockRow = { path: string; owner: string; leaseUntil: number };
export type GitLocksRes = { ok: boolean; cwd: string; locks: GitLockRow[]; count: number; expired: number };
export type AgentLockInfo = {
  path: string;
  owner: string;
  acquiredAt: number;
  leaseUntil: number;
  mode: string;
  reason?: string;
};
export type AgentLocksRes = {
  ok: boolean;
  agentId: string;
  agent: unknown;
  locks: AgentLockInfo[];
  expired: number;
  worktrees: string[];
};

// ─── Bots (shareable bot.json packages + run-as-agent, W5-20) ───

export type BotSource = 'bundled' | 'global' | 'project';
export type BotVisibility = 'private' | 'shared' | 'public';
export type BotMemoryScope = 'bot' | 'project' | 'user' | 'isolated';
export type BotBudgets = { maxTokens: number; maxUsd: number; maxTurns: number };
export type Bot = {
  id: string;
  name: string;
  avatar?: string;
  description: string;
  systemPrompt: string;
  model: string;
  fallback: string[];
  tools: string[];
  skills: string[];
  mcpServers: string[];
  knowledgeFiles: string[];
  memoryScope: BotMemoryScope;
  budgets: BotBudgets;
  visibility: BotVisibility;
  version: string;
  createdFrom?: string;
  tags: string[];
  author?: string;
  createdAt?: string;
  featured: boolean;
  source: BotSource;
};
export type BotsRes = { bots: BotWithSession[]; count: number };
/**
 * REQ-161 — `GET /api/bots?sessions=1` joins each bot's latest chat onto the
 * row (time + last-message preview + live run flags) for the Bots mode list.
 */
export type BotLastSession = {
  id: string;
  title: string;
  updatedAt: string | null;
  preview: string;
  running: boolean;
  queued: number;
};
export type BotWithSession = Bot & { lastSession?: BotLastSession | null };
export type BotDetailRes = { ok: boolean; bot: Bot };
export type CreateBotBody = {
  id?: string;
  name: string;
  description?: string;
  systemPrompt?: string;
  model?: string;
  fallback?: string[];
  tools?: string[];
  skills?: string[];
  mcpServers?: string[];
  knowledgeFiles?: string[];
  memoryScope?: BotMemoryScope;
  budgets?: Partial<BotBudgets>;
  visibility?: BotVisibility;
  version?: string;
  createdFrom?: string;
  tags?: string[];
  author?: string;
};
export type PatchBotBody = Partial<
  Pick<
    Bot,
    | 'name'
    | 'avatar'
    | 'description'
    | 'systemPrompt'
    | 'model'
    | 'fallback'
    | 'tools'
    | 'skills'
    | 'mcpServers'
    | 'knowledgeFiles'
    | 'memoryScope'
    | 'visibility'
    | 'version'
    | 'tags'
    | 'author'
  >
> & { budgets?: Partial<BotBudgets> };
export type BotMutationRes = { ok: boolean; bot: Bot; from?: string; visibility?: BotVisibility };
export type ForkBotBody = { as?: string };
export type PublishBotBody = { visibility: BotVisibility };
export type RunBotBody = { task: string; cwd?: string };
export type RunBotRes = {
  ok: boolean;
  agentId: string;
  agent: { id: string; [k: string]: unknown };
  sessionId: string;
};

// Auth + users + projects — RBAC matrix + visibility (W6-21, Docs/36).
// Roles mirror `lokma-shared` (superadmin/admin/calisan/viewer + legacy
// member); the server enforces every gate, the pane only mirrors the
// matrix for gating buttons.
export type AuthRole = 'superadmin' | 'admin' | 'calisan' | 'member' | 'viewer';
export type UserStatus = 'active' | 'invited' | 'disabled';
export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: AuthRole;
  status: UserStatus;
  permissions: string[];
  createdAt: string;
  lastActiveAt: string | null;
};
export type ProjectVisibility = 'private' | 'public';
export type AuthProject = {
  id: string;
  name: string;
  cwd: string;
  visibility: ProjectVisibility;
  ownerId: string;
  createdAt: string;
};
export type ProjectMember = {
  projectId: string;
  userId: string;
  role: 'calisan' | 'member' | 'viewer';
  permissions: string[];
  addedAt: string;
  addedBy: string;
};
export type AuthSettings = {
  projectCreation: 'admin-only' | 'members' | 'open';
  projectVisibilityDefault: ProjectVisibility;
  inviteExpiryDays: number;
  sessionRetentionDays: number | null;
  /** REQ-062 Parça A — full-screen login + WS token gate when true. */
  requireLogin: boolean;
  /** REQ-066 — first-run wizard done (server-persisted, unbootstrapped only). */
  onboardingDone: boolean;
};
export type AuthSessionRes = { ok: boolean; user: AuthUser; token: string };
export type AuthMeRes = { ok: boolean; user: AuthUser };
export type AuthSettingsRes = { ok: boolean; settings: AuthSettings; bootstrapped: boolean };
export type UsersRes = { users: AuthUser[]; count: number };
export type InviteRes = { ok: boolean; user: AuthUser; inviteLink: string };
export type ResetPasswordRes = { ok: boolean; user: AuthUser; tempPassword: string };
export type ProjectsRes = { projects: AuthProject[] };
export type ProjectDetailRes = { ok: boolean; project: AuthProject; members: ProjectMember[] };
export type ProjectMutationRes = { ok: boolean; project: AuthProject };
export type MembersRes = { members: ProjectMember[] };
export type MemberMutationRes = { ok: boolean; member: ProjectMember };

// ─── Todos + agent claims (REQ-062 Parça C, REQ-065, Docs/36 §11) ───

export type TodoStatus = 'open' | 'claimed' | 'done';
export type TodoClaimView = { sessionId: string; userId: string; claimedAt: string };
export type TodoView = {
  id: string;
  projectId: string;
  title: string;
  status: TodoStatus;
  claimedBy: TodoClaimView | null;
  leaseUntil: number | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
};
export type TodosRes = { todos: TodoView[]; count: number };
export type TodoMutationRes = { ok: boolean; todo: TodoView };

// ─── Plugins (kernel registry + hot toggle + add-from-URL, W6-23) ───

export type PluginSource = 'bundled' | 'url';
export type PluginCategory = 'core' | 'diagram' | 'tool' | 'skill';
export type Plugin = {
  id: string;
  name: string;
  version: string;
  author: string;
  description: string;
  category: PluginCategory;
  source: PluginSource;
  installed: true;
  enabled: boolean;
  routes: string[];
  endpoints: string[];
  url?: string;
};
export type PluginsRes = { plugins: Plugin[]; count: number };
export type PluginDetailRes = { ok: boolean; plugin: Plugin };
export type PluginMutationRes = { ok: boolean; plugin: Plugin };

/** One remote marketplace hit — a real GitHub `lokma-plugin` repo. */
export type MarketplaceItem = {
  repo: string;
  name: string;
  author: string;
  description: string;
  stars: number;
  url: string;
  updatedAt: string;
};
export type MarketplaceRes = { items: MarketplaceItem[]; count: number; source: string };

// ─── Observability (agent trace timeline + frozen share snapshots, W6-24) ───

export type TraceEventKind =
  | 'agent_created'
  | 'spawned'
  | 'agent_state'
  | 'soul_write'
  | 'memory_write'
  | 'lock_acquired';
export type TraceEventView = { ts: string; kind: TraceEventKind; label: string; detail?: string };
export type TraceLockView = {
  path: string;
  acquiredAt: string;
  leaseUntil: string;
  live: boolean;
  reason?: string;
};
export type TraceDocView = { exists: boolean; bytes: number; mtime: string | null };
/** `GET /api/agents/:id/trace` — the server spreads the trace at top level. */
export type AgentTraceRes = {
  agent: AgentInfo;
  events: TraceEventView[];
  locks: TraceLockView[];
  docs: { soul: TraceDocView; memory: TraceDocView };
  worktree: string | null;
  generatedAt: string;
};
export type ShareKind = 'agent' | 'session';
export type ShareSummaryView = {
  token: string;
  kind: ShareKind;
  refId: string;
  title: string;
  createdAt: string;
  /** Trace events (agent shares) or transcript rows (session shares). */
  size: number;
};
export type SharesRes = { shares: ShareSummaryView[]; count: number };
export type ShareCreateRes = { ok: boolean; token: string; url: string };
/** Transcript row as the Replay view renders it (narrowed from `unknown`). */
export type ReplayMessage = {
  role: 'user' | 'assistant' | 'tool';
  content: string;
  timestamp: string;
  toolName?: string;
};
export type ShareSessionSnapshot = {
  id: string;
  cwd: string;
  model: string | null;
  title: string;
  messages: ReplayMessage[];
  count: number;
};
export type ShareDetailRes = {
  share: {
    token: string;
    kind: ShareKind;
    refId: string;
    title: string;
    createdAt: string;
    snapshot: unknown;
  };
};

// Cron + approvals — per-agent jobs + WS decision history (W6-25, Docs/30
// §5 + §6). Schedules are server-validated 5-field cron; `nextRunAt` is
// computed server-side (null when disabled); `lastRunAt` is stamped by the
// agent-runner daemon on every fire (scheduled tick + Run-now) and every
// fire appends a `CronRunRecord` (see `GET /api/cron/runs`). Decisions
// fill as real WS answers arrive.
export type CronJobView = {
  id: string;
  agentId: string;
  schedule: string;
  task: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  lastRunAt: string | null;
  nextRunAt: string | null;
};
export type CronListRes = { jobs: CronJobView[]; count: number };
export type AgentCronRes = { jobs: CronJobView[]; count: number; agentId: string };
export type CronCreateBody = { schedule: string; task: string; enabled?: boolean };
export type CronPatchBody = { schedule?: string; task?: string; enabled?: boolean };
export type CronMutateRes = { ok: boolean; job: CronJobView };
export type CronRunRecordView = {
  runId: string;
  jobId: string;
  agentId: string;
  sessionId: string;
  trigger: 'schedule' | 'manual';
  startedAt: string;
  finishedAt: string;
  status: 'ok' | 'failed';
  chars: number;
  error?: string;
};
export type CronRunsRes = { runs: CronRunRecordView[]; count: number };
export type CronFireRes = { ok: boolean; job: CronJobView; run: CronRunRecordView };
export type ApprovalDecisionView = {
  id: string;
  at: string;
  source: 'ws' | 'manual';
  sessionId: string;
  kind: 'permission' | 'question';
  requestId: string;
  decision?: 'allow' | 'deny' | 'always';
  answer?: string;
};
export type ApprovalsRes = { decisions: ApprovalDecisionView[]; count: number };

// ─── Loops (REQ-200 model · REQ-201 executor · REQ-202 console) ─────────────

/**
 * One loop record, mirroring `LoopSchema` in `@lokma/shared`.
 *
 * The client re-declares the shape rather than importing it: `api.ts` is the
 * self-contained client layer and must not depend on a workspace package's
 * build output. Every field the console reads must exist here — a field the
 * server sends but this type omits reads as `undefined` in the UI and silently
 * renders as "no progress".
 *
 * `nextHint` is deliberately `string | null` and NOT defaulted: kapsam 2 renders
 * it verbatim as the "Sırada" row, and a fallback string would be an invented
 * queue position.
 */
export type LoopStatus = 'draft' | 'running' | 'paused' | 'done' | 'error';
export type LoopStopReason =
  | 'target_score'
  | 'max_iters'
  | 'max_hours'
  | 'budget'
  | 'idle'
  | 'stopped'
  | 'error';
export type LoopRunOutcome = 'ok' | 'empty' | 'aborted' | 'error';
export type LoopTriggerInput = {
  kind?: 'interval' | 'cron' | 'event' | 'manual';
  intervalMinutes?: number | null;
  schedule?: string | null;
  event?: 'file_changed' | 'commit' | null;
  path?: string | null;
  cooldownSeconds?: number | null;
  maxEmptyIters?: number | null;
};

export type LoopView = {
  id: string;
  /**
   * The loop's turn session — `sess_loop_<id>`, the executor's OWN derivation,
   * mapped onto every loop response by the route (REQ-204 slice 4). The office
   * reads a loop worker's tool rows from this session's transcript; clients
   * never re-derive it.
   */
  sessionId: string;
  name: string;
  projectId: string | null;
  cwd: string;
  prompt: string;
  origin: 'user' | 'agent';
  status: LoopStatus;
  /** Why it stopped — shown as a badge only when the loop is `done`/`error`. */
  stopReason: LoopStopReason | null;
  budget: { maxIters: number; maxHours: number; maxUsd: number };
  spent: { iters: number; hours: number; usd: number; tokens: number };
  /** Opaque score strings — the harness never pretends to know the scale. */
  score: { best: string | null; last: string | null; target: string | null };
  /** The loop's own "what is next", shown verbatim. Never a client default. */
  nextHint: string | null;
  trigger:
    | { kind: 'interval'; intervalMinutes: number }
    | { kind: 'cron'; schedule: string }
    | { kind: 'event'; event: 'file_changed' | 'commit'; path?: string | null }
    | { kind: 'manual' };
  cooldownSeconds: number;
  maxEmptyIters: number;
  lastRunOutcome: LoopRunOutcome | null;
  emptyIters: number;
  lastRunStartedAt: string | null;
  /** User pressed Stop while a turn was in flight (applied when it settles). */
  stopRequested: boolean;
  /** A turn was dispatched and has not been booked yet. */
  inFlightSince: string | null;
  model: string;
  reasoningEffort: string;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
};

export type LoopListRes = { loops: LoopView[]; count: number };
export type LoopMutationRes = { loop: LoopView };

/**
 * REQ-202 kapsam 3 — what the detail panel renders.
 *
 * `remaining: null` and `remaining: []` are DIFFERENT states and must stay so:
 * `null` = no `scope.md` exists (render the `nextHint` and no list), `[]` = the
 * file exists and everything is checked. Collapsing them makes a loop with no
 * scope file announce "0 items left" as if somebody had written a checklist.
 */
export type LoopDetailRes = {
  loop: LoopView;
  /** Last 20 ledger turns (the server tails it). */
  ledger: string;
  ledgerPath: string;
  /** RAW `state.json` text, byte-for-byte from disk — never re-serialized. */
  stateJson: string;
  statePath: string;
  hasScope: boolean;
  remaining: string[] | null;
};

export type LoopCreateBody = {
  name: string;
  cwd: string;
  prompt: string;
  projectId?: string | null;
  origin?: 'user' | 'agent';
  model?: string;
  reasoningEffort?: string;
  budget?: Partial<LoopView['budget']>;
  target?: string | null;
  nextHint?: string | null;
  trigger?: LoopTriggerInput;
};

/** Only editable fields — status/spent are NOT patchable (server-enforced). */
export type LoopPatchBody = Partial<Omit<LoopCreateBody, 'origin'>>;

/** `POST /:id/pause` — `deferred` means "finishing this turn", not "stopped". */
export type LoopPauseRes = { loop: LoopView; deferred: boolean };
/** `POST /:id/abort` — `cutTurn` means the in-flight turn was really cut. */
export type LoopAbortRes = { loop: LoopView; cutTurn: boolean };
/**
 * `POST /:id/run` answers 202: the turn runs in the BACKGROUND, so `accepted`
 * says "queued", never "finished". A refusal (404/409) still throws.
 */
export type LoopRunRes = { accepted: boolean; loop: LoopView; sessionId: string };
export type LoopDeleteRes = { ok: boolean; id: string };

// ─── One function per endpoint group ────────────────────────────────────────
// ─── One function per endpoint group ────────────────────────────────────────

/**
 * REQ-178 — design endpoints take an optional project `cwd`. Shared query
 * builder: joins with `&` when the path already carries a query string, and
 * never appends anything for the global root (`''` / undefined).
 */
function designCwdQuery(path: string, cwd?: string): string {
  if (!cwd) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}cwd=${encodeURIComponent(cwd)}`;
}

// ─── Loops (REQ-202 console) ───────────────────────────────────────────────
// Harness-owned loops only (REQ-200): Hermes' own `~/.hermes/loops/` trees are
// deliberately NOT catalogued, so this endpoint is the single source for both
// the Settings → Loops section and the Loops pane — one implementation, two
// entry points (kapsam 1).
const loopPath = (id: string) => `/api/loops/${encodeURIComponent(id)}`;

// ─── One function per endpoint group ────────────────────────────────────────

export const api = {
  // Health + config
  health: () => get<HealthRes>('/api/health'),
  /** Host metrics for the status bar (REQ-018: cpu/ram/version). */
  getMetrics: () => get<MetricsRes>('/api/metrics'),
  getConfig: () => get<ConfigRes>('/api/config'),
  config: () => get<ConfigRes>('/api/config'),
  patchConfig: (patchBody: Record<string, unknown>) =>
    patch<{ ok: boolean; patched: string[] }>('/api/config', patchBody),

  // Providers + models — CRUD + enable/disable are live server endpoints (W2).
  listProviders: () => get<ProvidersRes>('/api/providers'),
  providers: () => get<ProvidersRes>('/api/providers'),
  createProvider: (body: CreateProviderBody) => post<ProviderMutationRes>('/api/providers', body),
  patchProvider: (id: string, body: PatchProviderBody) =>
    patch<ProviderMutationRes>(`/api/providers/${encodeURIComponent(id)}`, body),
  deleteProvider: (id: string) => del<{ ok: boolean; id: string }>(`/api/providers/${encodeURIComponent(id)}`),
  reorderProviders: (order: string[]) =>
    post<{ ok: boolean; providers: ProviderInfo[] }>('/api/providers/reorder', { order }),
  testProvider: (id: string) => post<ProviderTestRes>(`/api/providers/${encodeURIComponent(id)}/test`),
  listModels: () => get<ModelsRes>('/api/models'),
  models: () => get<ModelsRes>('/api/models'),
  /** Enable/disable one model — server persists the flag to ~/.lokma/config.json. */
  setModelEnabled: (id: string, enabled: boolean) =>
    patch<ModelsMutationRes>('/api/models', { id, enabled }),
  /** Bulk enable/disable — one PATCH for Allow All / Disable All. */
  setModelsBulk: (models: Record<string, boolean>) =>
    patch<ModelsMutationRes>('/api/models', { models }),
  /** Live refresh — fans out to every enabled provider's /v1/models at once (REQ-032). */
  refreshModels: () => post<ModelsRefreshRes>('/api/models/refresh', {}),

  // Sessions — fork/patch/rewind are live server endpoints (W1 chat core).
  listSessions: (cwd?: string) =>
    get<SessionsRes>(cwd ? `/api/sessions?cwd=${encodeURIComponent(cwd)}` : '/api/sessions'),
  sessions: () => get<SessionsRes>('/api/sessions'),
  getSession: (id: string) => get<SessionDetail>(`/api/sessions/${encodeURIComponent(id)}`),
  /** REQ-070: is a backend run in flight (refresh-proof badge + polling)? */
  getSessionRun: (id: string) => get<SessionRunStatus>(`/api/sessions/${encodeURIComponent(id)}/run`),
  createSession: (body?: { cwd?: string; model?: string; botId?: string }) => post<CreateSessionRes>('/api/sessions', body ?? {}),
  forkSession: (id: string) => post<ForkSessionRes>(`/api/sessions/${encodeURIComponent(id)}/fork`),
  patchSession: (id: string, body: { model?: string; botId?: string | null }) =>
    patch<PatchSessionRes>(`/api/sessions/${encodeURIComponent(id)}`, body),
  renameSession: (id: string, title: string) =>
    patch<RenameSessionRes>(`/api/sessions/${encodeURIComponent(id)}`, { title }),
  deleteSession: (id: string) => del<DeleteSessionRes>(`/api/sessions/${encodeURIComponent(id)}`),
  mergeSessions: (intoId: string, fromId: string) =>
    post<MergeSessionRes>(`/api/sessions/${encodeURIComponent(intoId)}/merge`, { from: fromId }),
  rewindSession: (id: string, keepMessages: number) =>
    post<RewindSessionRes>(`/api/sessions/${encodeURIComponent(id)}/rewind`, { keepMessages }),
  // Compaction — two-tier Docs/28 §1.3 shrink (wave 3a: REST + client only,
  // pane UI rides with session_search in wave 3b).
  getCompactionStatus: (id: string, cwd?: string) =>
    get<CompactionStatusRes>(
      cwd
        ? `/api/sessions/${encodeURIComponent(id)}/compaction?cwd=${encodeURIComponent(cwd)}`
        : `/api/sessions/${encodeURIComponent(id)}/compaction`,
    ),
  runCompaction: (id: string, body?: { mode?: 'hygiene' | 'full'; cwd?: string }) =>
    post<CompactionRunRes>(
      body?.cwd
        ? `/api/sessions/${encodeURIComponent(id)}/compaction?cwd=${encodeURIComponent(body.cwd)}`
        : `/api/sessions/${encodeURIComponent(id)}/compaction`,
      body?.mode ? { mode: body.mode } : {},
    ),
  // Transcript search — FTS5 over one project's session transcripts (wave 3b,
  // Docs/28 session_search). The `engine` field says which backend answered.
  searchSessions: (q: string, opts?: { limit?: number; cwd?: string }) => {
    const params = new URLSearchParams({ q });
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.cwd) params.set('cwd', opts.cwd);
    return get<SessionSearchRes>(`/api/sessions/search?${params.toString()}`);
  },

  // Slash commands — server-owned registry behind the Composer `/` palette.
  listCommands: () => get<CommandsRes>('/api/commands'),

  // Agents — full registry CRUD + lifecycle (W4-13). The server owns
  // caps/queue/locks (429 when full); this store mirrors them for the HUD.
  listAgents: () => get<AgentsRes>('/api/agents'),
  getAgent: (id: string) => get<AgentInfo>(`/api/agents/${encodeURIComponent(id)}`),
  createAgent: (body: CreateAgentBody) => post<AgentMutationRes>('/api/agents', body),
  patchAgent: (id: string, body: PatchAgentBody) =>
    patch<AgentMutationRes>(`/api/agents/${encodeURIComponent(id)}`, body),
  deleteAgent: (id: string) => del<DeleteAgentRes>(`/api/agents/${encodeURIComponent(id)}`),
  /** Lifecycle move: pause | resume | kill (409 on illegal transitions). */
  moveAgent: (id: string, action: 'pause' | 'resume' | 'kill') =>
    post<AgentMutationRes>(`/api/agents/${encodeURIComponent(id)}/${action}`, {}),
  /** Copy the agent dir into a fresh id (state idle, same SOUL/MEMORY). */
  copyAgent: (id: string, action: 'fork' | 'clone') =>
    post<AgentMutationRes>(`/api/agents/${encodeURIComponent(id)}/${action}`, {}),
  /** Read the real SOUL.md / MEMORY.md file (`doc` = 'soul' | 'memory'). */
  getAgentDoc: (id: string, doc: 'soul' | 'memory') =>
    get<AgentDocRes>(`/api/agents/${encodeURIComponent(id)}/${doc}`),
  /** Persist SOUL.md / MEMORY.md (real file write under the agent dir). */
  saveAgentDoc: (id: string, doc: 'soul' | 'memory', content: string) =>
    put<AgentDocWriteRes>(`/api/agents/${encodeURIComponent(id)}/${doc}`, { content }),
  listSkills: () => get<SkillsRes>('/api/skills'),
  /** skill_view parity — full SKILL.md body + linked_files (records a view). */
  getSkill: (id: string) => get<SkillDetailRes>(`/api/skills/${encodeURIComponent(id)}`),
  /** Single reference load (progressive disclosure, jailed to the skill dir). */
  getSkillFile: (id: string, path: string) =>
    get<SkillFileRes>(`/api/skills/${encodeURIComponent(id)}/file?path=${encodeURIComponent(path)}`),
  /** Curator patch — exact old_string → new_string (single-occurrence guard). */
  patchSkill: (id: string, body: SkillPatchBody) =>
    patch<SkillPatchRes>(`/api/skills/${encodeURIComponent(id)}`, body),
  /** Record a use event (web parity of the agent loop's use event). */
  recordSkillUse: (id: string) => post<SkillUseRes>(`/api/skills/${encodeURIComponent(id)}/use`, {}),
  /** Live GitHub `lokma-skill` topic search (empty q browses the whole topic). */
  searchSkillMarketplace: (q = '') =>
    get<MarketplaceRes>(`/api/skills/marketplace${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  /** Strict https validation + git clone into ~/.lokma/skills (appears in registry). */
  installSkill: (url: string) => post<SkillInstallRes>('/api/skills/install', { url }),

  // Themes — canonical named-theme registry (Phase 3 themes polish).
  listThemes: () => get<ThemesRes>('/api/themes'),
  /** One theme def + preview (drives the Appearance cards + full var apply). */
  getTheme: (id: string) => get<ThemeDetailRes>(`/api/themes/${encodeURIComponent(id)}`),

  // Vault — live file graph + note reads + ingest (W4-15).
  getVaultGraph: (query?: string, opts?: { folder?: string; depth?: number }) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (opts?.folder) params.set('folder', opts.folder);
    if (opts?.depth !== undefined) params.set('depth', String(opts.depth));
    const suffix = params.size > 0 ? `?${params.toString()}` : '';
    return get<VaultGraphRes>(`/api/vault/graph${suffix}`);
  },
  /** FTS5 full-text search (BM25 score + snippet) — cheaper than graph for typeaheads. */
  searchVaultNotes: (query?: string, folder?: string) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (folder) params.set('folder', folder);
    const suffix = params.size > 0 ? `?${params.toString()}` : '';
    return get<VaultSearchRes>(`/api/vault/search${suffix}`);
  },
  /** Nested dir/note tree for the vault folder browser. */
  getVaultTree: (folder?: string) =>
    get<VaultTreeRes>(folder ? `/api/vault/tree?folder=${encodeURIComponent(folder)}` : '/api/vault/tree'),
  /** Full note read (wikilink click → pane). */
  getVaultNote: (path: string) => get<VaultNoteRes>(`/api/vault/note?path=${encodeURIComponent(path)}`),
  /** Ingest a `.md` note (`provenance` = the ingesting agent id). */
  ingestVaultNote: (body: VaultIngestBody) => post<VaultIngestRes>('/api/vault/ingest', body),
  /** Delete a `.md` note — the undo for ingest. */
  deleteVaultNote: (path: string) =>
    del<{ ok: boolean; path: string }>(`/api/vault/note?path=${encodeURIComponent(path)}`),

  // Memory — global §-delimited MEMORY.md / USER.md store (Docs/28 §5.2).
  /** Entries plus the live `chars/limit` budget line. */
  getMemory: (target: MemoryTarget = 'memory') =>
    get<MemoryUsageRes>(`/api/memory?target=${encodeURIComponent(target)}`),
  /** Append an entry (exact-dup is an idempotent ok; overflow is 409 `memory_full`). */
  addMemory: (body: MemoryAddBody) => post<MemoryUsageRes>('/api/memory', body),
  /** Replace the single entry containing `old_text` (0 → 404, 2+ → 409). */
  replaceMemory: (body: MemoryReplaceBody) => patch<MemoryUsageRes>('/api/memory', body),
  /** Remove the single entry containing `old_text` (0 → 404, 2+ → 409). */
  deleteMemory: (body: MemoryRemoveBody) =>
    request<MemoryUsageRes>('/api/memory', { method: 'DELETE', body: JSON.stringify(body) }),

  // Usage — real token/cost accounting (W2-7). The ledger fills from WS runs.
  getUsageSummary: (range: UsageRange = '7d', cwd?: string) =>
    get<UsageSummaryRes>(
      cwd
        ? `/api/usage/summary?range=${range}&cwd=${encodeURIComponent(cwd)}`
        : `/api/usage/summary?range=${range}`,
    ),
  getUsageSessions: (range: UsageRange = '7d', cwd?: string) =>
    get<UsageSessionsRes>(
      cwd
        ? `/api/usage/sessions?range=${range}&cwd=${encodeURIComponent(cwd)}`
        : `/api/usage/sessions?range=${range}`,
    ),
  /** Export download path — fetch it as a blob (keeps Bearer auth), not a plain link. */
  usageExportUrl: (format: UsageExportFormat, range: UsageRange = '7d') =>
    `/api/usage/export?format=${format}&range=${range}`,
  /** Real file download — blob + server filename, auth via `authedFetch`. */
  downloadUsageExport: async (
    format: UsageExportFormat,
    range: UsageRange = '7d',
  ): Promise<{ filename: string; blob: Blob }> => {
    const fallback = `lokma-usage-${range}.${format}`;
    const res = await authedFetch(`/api/usage/export?format=${format}&range=${range}`);
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    return { filename: match?.[1] ?? fallback, blob };
  },

  // Workspace files — real FS behind the FileBrowser pane (W3-9).
  listFiles: (cwd: string, path = '.') =>
    get<FilesRes>(`/api/files?cwd=${encodeURIComponent(cwd)}&path=${encodeURIComponent(path)}`),
  readWorkspaceFile: (cwd: string, path: string) =>
    get<FileContentRes>(`/api/files/read?cwd=${encodeURIComponent(cwd)}&path=${encodeURIComponent(path)}`),
  /** REQ-075: binary bytes for preview (pdf/images/html) — auth via authedFetch. */
  readWorkspaceFileRaw: async (cwd: string, path: string): Promise<Blob> => {
    const res = await authedFetch(`/api/files/raw?cwd=${encodeURIComponent(cwd)}&path=${encodeURIComponent(path)}`);
    if (!res.ok) throw await toApiError(res);
    return res.blob();
  },
  /** REQ-125: download bytes for ANY workspace file (not just previewable). */
  downloadWorkspaceFile: async (cwd: string, path: string): Promise<Blob> => {
    const res = await authedFetch(`/api/files/download?cwd=${encodeURIComponent(cwd)}&path=${encodeURIComponent(path)}`);
    if (!res.ok) throw await toApiError(res);
    return res.blob();
  },
  searchWorkspaceFiles: (cwd: string, q: string, max = 50) =>
    get<FileSearchRes>(
      `/api/files/search?cwd=${encodeURIComponent(cwd)}&q=${encodeURIComponent(q)}&max=${max}`,
    ),
  /** Save with the `expectedSha` lost-update guard (409 `stale_file` on conflict). */
  writeWorkspaceFile: (cwd: string, path: string, content: string, expectedSha?: string) =>
    post<FileWriteRes>('/api/files/write', {
      cwd,
      path,
      content,
      ...(expectedSha ? { expectedSha } : {}),
    }),
  /** REQ-114: PDF text extraction for composer attaches (pdftotext on server). */
  extractPdf: (body: { name: string; dataBase64: string }) =>
    post<{ ok: boolean; text: string; chars: number; truncated: boolean }>('/api/attachments/extract', body),
  /** REQ-120: delete one file or dir tree (jailed server-side). */
  deleteWorkspaceFile: (cwd: string, path: string) =>
    del<{ ok: boolean; path: string; wasDir: boolean }>(
      `/api/files?cwd=${encodeURIComponent(cwd)}&path=${encodeURIComponent(path)}`,
    ),
  /** REQ-120: rename/move inside the workspace (both paths jailed). */
  renameWorkspaceFile: (cwd: string, oldPath: string, newPath: string) =>
    post<{ ok: boolean; path: string }>('/api/files/rename', { cwd, oldPath, newPath }),
  /** REQ-084: server directory browser for the project folder picker. */
  listDirs: (path?: string) =>
    get<FsListRes>(path === undefined ? '/api/fs/list' : `/api/fs/list?path=${encodeURIComponent(path)}`),

  // Terminals — live shells (W3-10). Bytes flow over WS `terminal/*` frames;
  // REST only spawns/lists/inspects/kills.
  listTerminals: () => get<TerminalsRes>('/api/terminal'),
  createTerminal: (body: CreateTerminalBody) => post<CreateTerminalRes>('/api/terminal', body),
  getTerminal: (id: string) => get<TerminalDetailRes>(`/api/terminal/${encodeURIComponent(id)}`),
  /** Non-WS stdin path (CLI parity + probes) — the pane sends over WS. */
  sendTerminalInput: (id: string, data: string) =>
    post<TerminalInputRes>(`/api/terminal/${encodeURIComponent(id)}/input`, { data }),
  deleteTerminal: (id: string) => del<DeleteTerminalRes>(`/api/terminal/${encodeURIComponent(id)}`),

  // Repo git — real status/log/commit/push (W3-11). All calls scope to the
  // session cwd, like the file endpoints.
  getGitStatus: (cwd?: string) =>
    get<GitStatusRes>(cwd ? `/api/git/status?cwd=${encodeURIComponent(cwd)}` : '/api/git/status'),
  getGitLog: (cwd?: string, max = 20) =>
    get<GitLogRes>(
      cwd
        ? `/api/git/log?cwd=${encodeURIComponent(cwd)}&max=${max}`
        : `/api/git/log?max=${max}`,
    ),
  /** Stage-all + commit (server returns the new hash). */
  commitGit: (message: string, cwd?: string) =>
    post<GitCommitRes>('/api/git/commit', { message, ...(cwd ? { cwd } : {}) }),
  pushGit: (cwd?: string) => post<GitPushRes>('/api/git/push', cwd ? { cwd } : {}),
  /** Worktree prune (the pane GC button). */
  gcGit: (cwd?: string) => post<GitGcRes>('/api/git/gc', cwd ? { cwd } : {}),
  /** Live locks under the repo (rows + banner). */
  getGitLocks: (cwd?: string) =>
    get<GitLocksRes>(cwd ? `/api/git/locks?cwd=${encodeURIComponent(cwd)}` : '/api/git/locks'),
  /** Per-agent 3-layer safety state (plan §W3-11, reused by W4 AgentHub). */
  getAgentLocks: (id: string, cwd?: string) =>
    get<AgentLocksRes>(
      cwd
        ? `/api/agents/${encodeURIComponent(id)}/locks?cwd=${encodeURIComponent(cwd)}`
        : `/api/agents/${encodeURIComponent(id)}/locks`,
    ),

  // Browser tabs — per-agent live tabs (W3-12). The server owns tabs +
  // history; pages render live in the client iframe (no page bytes on REST).
  listBrowserTabs: (sessionId?: string) =>
    get<BrowserTabsRes>(
      sessionId ? `/api/browser?sessionId=${encodeURIComponent(sessionId)}` : '/api/browser',
    ),
  openBrowserTab: (body: OpenBrowserTabBody) => post<OpenBrowserTabRes>('/api/browser/open', body),
  getBrowserTab: (id: string) => get<BrowserTabRes>(`/api/browser/${encodeURIComponent(id)}`),
  /** Address-bar go — pushes real history (drops forward entries). */
  navigateBrowserTab: (id: string, url: string) =>
    post<BrowserTabRes>(`/api/browser/${encodeURIComponent(id)}/navigate`, { url }),
  backBrowserTab: (id: string) => post<BrowserTabRes>(`/api/browser/${encodeURIComponent(id)}/back`, {}),
  forwardBrowserTab: (id: string) =>
    post<BrowserTabRes>(`/api/browser/${encodeURIComponent(id)}/forward`, {}),
  /** Server touch — the client re-sets the frame (see BrowserPane reload key). */
  reloadBrowserTab: (id: string) =>
    post<BrowserTabRes>(`/api/browser/${encodeURIComponent(id)}/reload`, {}),
  closeBrowserTab: (id: string) =>
    del<CloseBrowserTabRes>(`/api/browser/${encodeURIComponent(id)}`),

  /**
   * REQ-193 slice 9 — ask the proxy what it just served, WITHOUT taking the
   * body. The pane's iframe is sandboxed (`allow-scripts allow-same-origin`), so
   * its response headers are unreachable from the frame: reading them would mean
   * guessing the proxy URL's identity through `contentDocument`, which is null
   * for a sandboxed document (the REQ-152 lesson). Asking the server for just the
   * verdict is one cheap request and keeps the wall a FIELD rather than
   * something the pane re-derives by parsing a page.
   */
  probeBrowserPage: async (url: string): Promise<BrowserPageProbe> => {
    // Range bytes=0-0 keeps the response to a single byte instead of
    // re-shipping a multi-megabyte document the iframe is already rendering.
    const res = await authedFetch(`${BROWSER_PROXY_PATH}?url=${encodeURIComponent(url)}`, {
      headers: { range: 'bytes=0-0' },
    });
    return {
      status: res.status,
      finalUrl: res.headers.get('x-lokma-proxy-url') ?? url,
      loginWall: res.headers.get(LOGIN_WALL_HEADER) === '1',
    };
  },

  // Archify diagrams — typed IR → validated HTML/SVG (W5-17). The server
  // owns validation + rendering; the pane edits IR and downloads artifacts.
  listDiagrams: () => get<DiagramsRes>('/api/archify/list'),
  generateDiagram: (body: GenerateDiagramBody) => post<GenerateDiagramRes>('/api/archify/generate', body),
  /** 5-gate check without saving (the pane receipt preview). */
  validateDiagram: (ir: ArchifyIR) => post<ValidateDiagramRes>('/api/archify/validate', { ir }),
  getDiagram: (id: string) => get<DiagramDetailRes>(`/api/archify/${encodeURIComponent(id)}`),
  /** Persist an edited IR — server validates + rebuilds viewer/exports. */
  saveDiagram: (id: string, ir: ArchifyIR) =>
    request<SaveDiagramRes>(`/api/archify/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ ir }),
    }),
  /** Remove the whole on-disk dir (ir + viewer + exports + receipt + delta). */
  deleteDiagram: (id: string) =>
    del<{ ok: boolean; id: string }>(`/api/archify/${encodeURIComponent(id)}`),
  /** Before/Delta/After against another diagram (writes delta.html). */
  compareDiagrams: (id: string, baseId: string) =>
    post<DiagramDeltaRes>(`/api/archify/${encodeURIComponent(id)}/delta`, { baseId }),
  /** Starter chain for a topic (fills the generate prompt, editable). */
  getArchifyGuide: (id: string, topic?: string) =>
    get<ArchifyGuideRes>(
      topic
        ? `/api/archify/${encodeURIComponent(id)}/guide?topic=${encodeURIComponent(topic)}`
        : `/api/archify/${encodeURIComponent(id)}/guide`,
    ),
  /** Stable viewer URL (deep-link hashes work on a real URL, not srcDoc). */
  archifyViewUrl: (id: string) => `/api/archify/${encodeURIComponent(id)}/view`,
  /** Real file download — blob + server filename, auth via `authedFetch`. */
  downloadArchifyExport: async (
    id: string,
    format: ArchifyExportFormat,
    scale?: 1 | 2,
  ): Promise<{ filename: string; blob: Blob }> => {
    const fallback = `${id}.${format === 'card' ? 'card.svg' : format}`;
    const suffix = format === 'png' && scale !== undefined ? `&scale=${scale}` : '';
    const res = await authedFetch(`/api/archify/${encodeURIComponent(id)}/export?format=${format}${suffix}`);
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    return { filename: match?.[1] ?? fallback, blob };
  },

  // Design Studio — 6 artifact types over bundled systems (W5-18). The
  // server owns generation + guard + critique + rendering; the pane lists,
  // previews, edits HTML and downloads artifacts.
  //
  // REQ-178 — every entry point takes an optional project `cwd`
  // ('' / undefined = the global `~/.lokma/design/artifacts` root). The
  // server resolves it strictly (404 `cwd_not_found` / 400
  // `not_a_directory`), so only a picked value is ever sent.
  listDesigns: (cwd?: string) => get<DesignsRes>(designCwdQuery('/api/design/list', cwd)),
  generateDesign: (body: GenerateDesignBody) => post<GenerateDesignRes>('/api/design/generate', body),
  getDesign: (id: string, cwd?: string) =>
    get<DesignDetailRes>(designCwdQuery(`/api/design/${encodeURIComponent(id)}`, cwd)),
  /**
   * Persist an edited HTML document — server validates + re-critiques.
   * REQ-190: `expectedSha` is the optimistic lock, exactly like the tweak's —
   * a pane holding a body the server has since changed is refused with 409
   * `stale_version` instead of overwriting a version the user never saw.
   */
  saveDesignHtml: (id: string, html: string, cwd?: string, expectedSha?: string) =>
    request<SaveDesignRes>(`/api/design/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ html, ...(cwd ? { cwd } : {}), ...(expectedSha ? { expectedSha } : {}) }),
    }),
  /** Remove the whole on-disk dir (artifact.json + html + design.md + critique). */
  deleteDesign: (id: string, cwd?: string) =>
    del<{ ok: boolean; id: string }>(designCwdQuery(`/api/design/${encodeURIComponent(id)}`, cwd)),
  /** Re-run the 5D heuristic critique over the stored HTML. */
  critiqueDesign: (id: string, cwd?: string) =>
    post<CritiqueDesignRes>(designCwdQuery(`/api/design/${encodeURIComponent(id)}/critique`, cwd), {}),
  /**
   * REQ-190 — the artifact's version history (oldest first) + which entry is
   * current. A pre-REQ-190 artifact answers `versions: []` and
   * `currentVersion: 0`, so the picker renders an honest empty state instead of
   * inventing a v1.
   */
  getDesignVersions: (id: string, cwd?: string) =>
    get<DesignVersionsRes>(designCwdQuery(`/api/design/${encodeURIComponent(id)}/versions`, cwd)),
  /**
   * REQ-190 — tweak the artifact in place: the result lands as the NEXT
   * version of THIS id, so the artifact list never grows and the previous body
   * stays recoverable. `expectedSha` is the optimistic lock (409
   * `stale_version`) checked before the metered model call.
   */
  tweakDesign: (id: string, body: DesignTweakBody) =>
    post<DesignTweakRes>(`/api/design/${encodeURIComponent(id)}/tweak`, body),
  /**
   * REQ-190 — go back to an earlier version. The server restores the archived
   * body as a NEW current version (a `revert` entry), so nothing is destroyed
   * and a redo is just another revert forward.
   */
  revertDesign: (id: string, version: number, cwd?: string) =>
    post<DesignRevertRes>(designCwdQuery(`/api/design/${encodeURIComponent(id)}/revert`, cwd), {
      version,
      ...(cwd ? { cwd } : {}),
    }),
  /** 4 bundled system cards (name/preset/tokens for the picker). */
  getDesignSystems: () => get<DesignSystemsRes>('/api/design/systems'),
  /**
   * REQ-192 — the design-SKILL catalog: installed `scope: design` skills,
   * grouped and searchable. Separate from `/systems` on purpose (a system is a
   * palette, a skill is a `SKILL.md` instruction set) and never merged into it.
   */
  getDesignSkills: () => get<DesignSkillsRes>('/api/design/skills'),
  /**
   * REQ-192 slice 5 — the design-TEMPLATE catalog (the third axis): the
   * output skeleton each artifact ships as. Read-only here; slice 4 added
   * `POST /api/design/templates` for installs, driven from the CLI.
   */
  getDesignTemplates: () => get<DesignTemplatesRes>('/api/design/templates'),
  /**
   * REQ-192 slice 5 — ONE template's body for the composer preview. Same
   * resolver the generation path uses, so a previewed body is byte-identical
   * to the body that would reach the model.
   */
  getDesignTemplate: (id: string) =>
    get<{ ok: boolean; template: { id: string; label: string; mode: string; content: string } }>(
      `/api/design/templates/${encodeURIComponent(id)}`,
    ),
  /** REQ-191 — install one package into the catalog (local path or https URL). */
  installDesignSystem: (source: string) => post<DesignSystemInstallRes>('/api/design/systems', { source }),
  /** REQ-191 — activate an installed system in the project `.lokma/`. */
  useDesignSystem: (id: string, cwd?: string) =>
    post<DesignSystemUseRes>(`/api/design/systems/${encodeURIComponent(id)}/use`, { cwd }),
  /** Real `.lokma/DESIGN.md` guard for the picked project (else the server cwd). */
  getDesignGuard: (cwd?: string) => get<DesignGuardRes>(designCwdQuery('/api/design/guard', cwd)),
  /** Stable viewer URL (sandboxed iframe, self-contained HTML, no CDN). */
  designViewUrl: (id: string, cwd?: string) =>
    designCwdQuery(`/api/design/${encodeURIComponent(id)}/view`, cwd),
  /** Real file download — blob + server filename, auth via `authedFetch`. */
  downloadDesignExport: async (
    id: string,
    format: DesignExportFormat,
    scale?: 1 | 2,
    cwd?: string,
  ): Promise<{ filename: string; blob: Blob }> => {
    const fallback = `${id}.${format}`;
    const suffix = format === 'png' && scale !== undefined ? `&scale=${scale}` : '';
    const res = await authedFetch(
      designCwdQuery(`/api/design/${encodeURIComponent(id)}/export?format=${format}${suffix}`, cwd),
    );
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    return { filename: match?.[1] ?? fallback, blob };
  },

  // Testing Lab — Plan→Run→Classify→Report (W5-19). The server owns
  // execution (one real GET per target) + classification + junit; the pane
  // lists runs, starts new ones, and downloads the Report-stage XML.
  listTestRuns: () => get<TestsRes>('/api/tests/list'),
  runTestPlan: (body: RunTestBody) => post<RunTestRes>('/api/tests/run', body),
  getTestRun: (id: string) => get<TestDetailRes>(`/api/tests/${encodeURIComponent(id)}`),
  /** Remove the whole on-disk run dir (plan.json + report.json + junit.xml). */
  deleteTestRun: (id: string) =>
    del<{ ok: boolean; id: string }>(`/api/tests/${encodeURIComponent(id)}`),
  /** Report-stage `junit.xml` — blob + server filename, auth via `authedFetch`. */
  downloadTestJunit: async (id: string): Promise<{ filename: string; blob: Blob }> => {
    const fallback = `${id}.junit.xml`;
    const res = await authedFetch(`/api/tests/${encodeURIComponent(id)}/junit`);
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    return { filename: match?.[1] ?? fallback, blob };
  },

  // Bots — shareable bot.json packages (W5-20). The server owns the
  // registry (`~/.lokma/bots/` + bundled lokma-ceo); run spawns a real
  // agent + a real session for playground chat.
  listBots: (opts?: { sessions?: boolean }) =>
    get<BotsRes>(opts?.sessions ? '/api/bots?sessions=1' : '/api/bots'),
  getBot: (id: string) => get<BotDetailRes>(`/api/bots/${encodeURIComponent(id)}`),
  createBot: (body: CreateBotBody) => post<BotMutationRes>('/api/bots', body),
  /** Edit a user bot (bundled templates answer 400 `bundled_readonly`). */
  patchBot: (id: string, body: PatchBotBody) =>
    patch<BotMutationRes>(`/api/bots/${encodeURIComponent(id)}`, body),
  /** Clone into a new private bot (`as` optional — server derives one). */
  forkBot: (id: string, body: ForkBotBody = {}) =>
    post<BotMutationRes>(`/api/bots/${encodeURIComponent(id)}/fork`, body),
  /** Flip gallery visibility (shared/public legs). */
  publishBot: (id: string, body: PublishBotBody) =>
    post<BotMutationRes>(`/api/bots/${encodeURIComponent(id)}/publish`, body),
  /** Spawn a real agent + session from the bot (playground run). */
  runBot: (id: string, body: RunBotBody) =>
    post<RunBotRes>(`/api/bots/${encodeURIComponent(id)}/run`, body),
  /** Remove a user bot (bundled rows answer 400 `bundled_readonly`). */
  deleteBot: (id: string) => del<{ ok: boolean; id: string }>(`/api/bots/${encodeURIComponent(id)}`),

  // Auth + users + projects — RBAC matrix + visibility (W6-21, Docs/36).
  // Login/register return the token twice: httpOnly cookie (set by the
  // server) + JSON body (stored in `localStorage["lokma-token"]` for the
  // Bearer path the CLI also uses).
  registerFirstAdmin: (body: { email: string; name: string; password: string }) =>
    post<AuthSessionRes>('/api/auth/register', body),
  login: (body: { email: string; password: string }) => post<AuthSessionRes>('/api/auth/login', body),
  logout: () => post<{ ok: boolean }>('/api/auth/logout', {}),
  authMe: () => get<AuthMeRes>('/api/auth/me'),
  /** Logged-out check — 401 expected, never bounces to /login. */
  authMeQuiet: () => request<AuthMeRes>('/api/auth/me', { method: 'GET' }, { redirect401: false }),
  acceptInvite: (body: { token: string; name: string; password: string }) =>
    post<AuthSessionRes>('/api/auth/accept-invite', body),
  /** Instance policy — public read (bootstrapped flag included), admin write. */
  getAuthSettings: () => get<AuthSettingsRes>('/api/auth/settings'),
  patchAuthSettings: (body: Partial<AuthSettings>) =>
    patch<AuthSettingsRes>('/api/auth/settings', body),
  /**
   * REQ-066 — record the first-run choice on a FRESH instance only
   * (403 once bootstrapped). The open-path wizard calls this with
   * `{ requireLogin: false }`; the auth path never does (register +
   * authed PATCH instead).
   */
  completeOnboarding: (body: { requireLogin: boolean }) =>
    post<{ ok: boolean; settings: AuthSettings; bootstrapped: boolean }>('/api/auth/onboarding', body),
  /** Admin user table (403 for member/viewer). */
  listUsers: () => get<UsersRes>('/api/users'),
  inviteUser: (body: { email: string; role?: AuthRole; projectIds?: string[] }) =>
    post<InviteRes>('/api/users/invite', body),
  patchUser: (id: string, body: Record<string, unknown>) =>
    patch<AuthMeRes>(`/api/users/${encodeURIComponent(id)}`, body),
  deleteUser: (id: string) => del<{ ok: boolean; id: string }>(`/api/users/${encodeURIComponent(id)}`),
  resetUserPassword: (id: string) =>
    post<ResetPasswordRes>(`/api/users/${encodeURIComponent(id)}/reset-password`, {}),
  /** Projects — visibility-filtered list; create/delete per RBAC policy. */
  listProjects: () => get<ProjectsRes>('/api/projects'),
  createProject: (body: { name: string; cwd?: string; visibility?: ProjectVisibility }) =>
    post<ProjectMutationRes>('/api/projects', body),
  getProject: (id: string) => get<ProjectDetailRes>(`/api/projects/${encodeURIComponent(id)}`),
  patchProject: (id: string, body: Record<string, unknown>) =>
    patch<ProjectMutationRes>(`/api/projects/${encodeURIComponent(id)}`, body),
  deleteProject: (id: string) => del<{ ok: boolean; id: string }>(`/api/projects/${encodeURIComponent(id)}`),
  /** Project members — invite/add/remove (requires `project:edit`). */
  listMembers: (projectId: string) =>
    get<MembersRes>(`/api/projects/${encodeURIComponent(projectId)}/members`),
  addMember: (projectId: string, body: { userId: string; role?: 'calisan' | 'member' | 'viewer' }) =>
    post<MemberMutationRes>(`/api/projects/${encodeURIComponent(projectId)}/members`, body),
  removeMember: (projectId: string, userId: string) =>
    del<{ ok: boolean; id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    ),

  // Todos + agent claims — per-project board (REQ-062 Parça C, Docs/36 §11).
  /** Board list (expired claims auto-release server-side on read). */
  listTodos: (projectId: string) => get<TodosRes>(`/api/projects/${encodeURIComponent(projectId)}/todos`),
  /** Create an open todo. */
  createTodo: (projectId: string, body: { title: string }) =>
    post<TodoMutationRes>(`/api/projects/${encodeURIComponent(projectId)}/todos`, body),
  /** Atomic open→claimed (409 `todo_claimed` + holder when чужой lease live). */
  claimTodo: (projectId: string, todoId: string, body: { sessionId: string }) =>
    post<TodoMutationRes>(
      `/api/projects/${encodeURIComponent(projectId)}/todos/${encodeURIComponent(todoId)}/claim`,
      body,
    ),
  /** Extend the holder lease (agent heartbeats call this, not the model). */
  heartbeatTodo: (projectId: string, todoId: string, body: { sessionId: string }) =>
    post<{ ok: boolean; extended: boolean }>(
      `/api/projects/${encodeURIComponent(projectId)}/todos/${encodeURIComponent(todoId)}/heartbeat`,
      body,
    ),
  /** Mark done (holder session or expired lease required). */
  completeTodo: (projectId: string, todoId: string, body: { sessionId?: string }) =>
    post<TodoMutationRes>(
      `/api/projects/${encodeURIComponent(projectId)}/todos/${encodeURIComponent(todoId)}/complete`,
      body,
    ),
  /** Release a claim back to open. */
  releaseTodo: (projectId: string, todoId: string, body: { sessionId?: string }) =>
    post<TodoMutationRes>(
      `/api/projects/${encodeURIComponent(projectId)}/todos/${encodeURIComponent(todoId)}/release`,
      body,
    ),
  /** Delete a todo row. */
  deleteTodo: (projectId: string, todoId: string) =>
    del<{ ok: boolean; id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/todos/${encodeURIComponent(todoId)}`,
    ),

  // Setup + doctor — optional stack + init + subsystem probes (W6-22,
  // Docs/32 §8). The server owns the registry, the flags, init and every
  // probe; the pane only renders and persists.
  /** Feature registry + resolved flags (stored flags win over defaults). */
  getSetup: () => get<SetupRes>('/api/setup'),
  /** Persist the checkbox map (unknown ids answer 400 `unknown_feature`). */
  saveSetupFeatures: (features: Record<string, boolean>) =>
    post<SetupSaveRes>('/api/setup', { features }),
  /** Ensure global config + data dirs exist (missing ones are created). */
  initSetup: (body: SetupInitBody = {}) => post<SetupInitRes>('/api/setup/init', body),
  /** 8 subsystem probes (+ SOUL when `agents` is true) — all measured live. */
  getDoctor: (agents = false) => get<DoctorRes>(agents ? '/api/doctor?agents=1' : '/api/doctor'),
  /** Portable state bundle — blob + server filename, auth via `authedFetch`. */
  downloadCloudExport: async (): Promise<{ filename: string; blob: Blob }> => {
    const fallback = 'lokma-state.zip';
    const res = await authedFetch('/api/cloud/export', { method: 'POST' });
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    return { filename: match?.[1] ?? fallback, blob };
  },
  /** Restore a state bundle (existing files are kept unless overwrite is true). */
  importCloudState: (body: CloudImportBody) => post<CloudImportRes>('/api/cloud/import', body),

  // ─── Public tunnel (REQ-193 Kapsam 4) ─────────────────────────────────────
  // Three thin consumers of the routes in `routes/cloud.ts`. The honesty
  // contract is the server's: these types exist so the panel cannot invent a
  // field, and `url` is nullable because the server only ever fills it from
  // provider stdout — never a fabricated host.
  getTunnelStatus: () => get<TunnelStatusRes>('/api/cloud/tunnel'),
  startTunnel: (body: TunnelStartBody = {}) => post<TunnelStatusRes>('/api/cloud/tunnel/start', body),
  stopTunnel: () => post<TunnelStatusRes>('/api/cloud/tunnel/stop'),

  // Plugins — kernel registry + hot toggle + add-from-URL (W6-23, Docs/23).
  // Toggle suspends the plugin's routes server-side (503), no restart.
  /** Bundled manifests (real endpoint lists) + URL-installed records. */
  listPlugins: () => get<PluginsRes>('/api/plugins'),
  /** Manifest detail (same record, re-read live for the Kernel expander). */
  getPlugin: (id: string) => get<PluginDetailRes>(`/api/plugins/${encodeURIComponent(id)}`),
  /** Hot enable/disable (suspends/resumes the plugin's routes instantly). */
  setPluginEnabled: (id: string, enabled: boolean) =>
    patch<PluginMutationRes>(`/api/plugins/${encodeURIComponent(id)}`, { enabled }),
  /** Strict https validation; stored suspended until explicitly enabled. */
  installPlugin: (url: string) => post<PluginMutationRes>('/api/plugins/install', { url }),
  /** URL records only — bundled rows answer 400 `bundled_readonly`. */
  deletePlugin: (id: string) => del<{ ok: boolean; id: string }>(`/api/plugins/${encodeURIComponent(id)}`),
  /** Live GitHub `lokma-plugin` topic search (empty q browses the whole topic). */
  searchMarketplace: (q = '') =>
    get<MarketplaceRes>(`/api/plugins/marketplace${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  // Observability — agent trace timeline + frozen share snapshots (W6-24).
  // Trace events are derived from durable state server-side (registry +
  // doc mtimes + locks + lineage); shares freeze the trace/transcript so
  // later edits never rewrite shared history.
  /** Live timeline for one agent (404 `agent_not_found`, 400 `bad_agent_id`). */
  getAgentTrace: (id: string) => get<AgentTraceRes>(`/api/agents/${encodeURIComponent(id)}/trace`),
  /** Share metadata list (no snapshot bytes — loaded per share). */
  listShares: () => get<SharesRes>('/api/share'),
  /** Freeze an agent trace — returns the public copyable `/share/<token>` URL. */
  shareAgent: (agentId: string) => post<ShareCreateRes>('/api/share/agent', { agentId }),
  /** Freeze a session transcript — returns the public `/share/<token>` URL. */
  shareSession: (sessionId: string, cwd?: string) =>
    post<ShareCreateRes>('/api/share/session', cwd ? { sessionId, cwd } : { sessionId }),
  /** Read one frozen snapshot (serves the copy, never re-derives). */
  getShare: (token: string) => get<ShareDetailRes>(`/api/share/${encodeURIComponent(token)}`),
  /** Drop a share (source agent/session untouched). */
  deleteShare: (token: string) => del<{ ok: boolean; token: string }>(`/api/share/${encodeURIComponent(token)}`),

  // Cron + approvals — per-agent jobs + WS decision history (W6-25).
  // Toggle/delete/create hit live routes; the Allow/Deny/Always RULES live
  // in `getConfig`/`patchConfig` permissions (same store the chat card
  // writes — one store, two views); history is read-only and real.
  /** All jobs, newest first (pane header counts + list). */
  listCronJobs: () => get<CronListRes>('/api/cron'),
  /** One agent's jobs (404 `agent_not_found`). */
  listAgentCron: (agentId: string) => get<AgentCronRes>(`/api/agents/${encodeURIComponent(agentId)}/cron`),
  /** Create a job (server mints the id; 400 `bad_schedule`/`bad_task`). */
  createCronJob: (agentId: string, body: CronCreateBody) =>
    post<CronMutateRes>(`/api/agents/${encodeURIComponent(agentId)}/cron`, body),
  /** Edit schedule/task/enabled (empty → 400 `empty_patch`). */
  patchCronJob: (agentId: string, jobId: string, body: CronPatchBody) =>
    patch<CronMutateRes>(`/api/agents/${encodeURIComponent(agentId)}/cron/${encodeURIComponent(jobId)}`, body),
  /** Delete a job (unknown → 404, never silent). */
  deleteCronJob: (agentId: string, jobId: string) =>
    del<{ ok: boolean; id: string }>(`/api/agents/${encodeURIComponent(agentId)}/cron/${encodeURIComponent(jobId)}`),
  /** Fire a job NOW (streams the agent model into a cron session + stamps lastRunAt). */
  runCronJob: (agentId: string, jobId: string) =>
    post<CronFireRes>(`/api/agents/${encodeURIComponent(agentId)}/cron/${encodeURIComponent(jobId)}/run`, {}),
  /** Newest-first run history (every fire lands one record). */
  listCronRuns: () => get<CronRunsRes>('/api/cron/runs'),
  /** Newest-first WS decision history (fills as real answers arrive). */
  listApprovals: () => get<ApprovalsRes>('/api/approvals'),
// Loops — REQ-202 kapsam 1: the ONE implementation behind both the Settings
  // → Loops section and the Loops pane. Every control here is a live route;
  // no console state is invented client-side.
  //
  // Kapsam 5 (live update) rides the WS `agent_state` frame + a 5s poll of
  // `listLoops`; this slice ships the data layer the reducer will drive.
  /** All harness loops, newest first (`[]` = empty catalog, kapsam 7). */
  listLoops: () => get<LoopListRes>('/api/loops'),
  /** REQ-203: project-scoped view; `'-'` means loops with NO project. */
  listProjectLoops: (projectId: string) =>
    get<LoopListRes>(`/api/loops?projectId=${encodeURIComponent(projectId)}`),
  /** One record (404 `loop_not_found`, never a silent empty object). */
  getLoop: (id: string) => get<{ loop: LoopView }>(loopPath(id)),
  /**
   * Detail payload (kapsam 3): ledger tail + the RAW `state.json` text +
   * `scope.md` remaining items. `remaining === null` means NO scope file —
   * distinct from `[]`, which means a file whose items are all checked.
   */
  getLoopDetail: (id: string) => get<LoopDetailRes>(`${loopPath(id)}/detail`),
  /** Create in `draft` (server mints the id; 201). */
  createLoop: (body: LoopCreateBody) => post<LoopMutationRes>('/api/loops', body),
  /** Editable fields only — status/spent are rejected by the server. */
  patchLoop: (id: string, body: LoopPatchBody) => patch<LoopMutationRes>(loopPath(id), body),
  /**
   * Fire ONE turn now — answers 202, the turn runs in the BACKGROUND. So the
   * panel must say "queued", never "finished"; refusals (404/409) throw and
   * are reported as rejections, not as accepted runs.
   */
  runLoop: (id: string, body?: { force?: boolean }) => post<LoopRunRes>(`${loopPath(id)}/run`, body ?? {}),
  /**
   * Pause — finishes the turn in flight and answers `deferred: true`, so the UI
   * says "finishing this turn" instead of claiming the agent already stopped.
   */
  pauseLoop: (id: string) => post<LoopPauseRes>(`${loopPath(id)}/pause`, {}),
  /** Abort — cuts the in-flight turn (`cutTurn: true`), unlike pause. */
  abortLoop: (id: string) => post<LoopAbortRes>(`${loopPath(id)}/abort`, {}),
  /** `paused → running` (409 on an illegal transition). */
  resumeLoop: (id: string) => post<LoopMutationRes>(`${loopPath(id)}/resume`, {}),
  /** Remove the loop's directory; history is kept server-side. */
  deleteLoop: (id: string) => del<LoopDeleteRes>(loopPath(id)),
  /**
   * REQ-202 kapsam 4 — `Open ledger (md)`: the raw `ledger.md` as a download.
   *
   * The loop directory lives OUTSIDE every workspace jail, so this is its own
   * route rather than `/api/files/download?cwd=`. Blob-based because the call is
   * authenticated (a plain `window.open` would drop the Bearer header).
   */
  downloadLoopLedger: async (id: string): Promise<Blob> => {
    const res = await authedFetch(`${loopPath(id)}/ledger`);
    if (!res.ok) throw await toApiError(res);
    return res.blob();
  },
};
